import ApiService from '@/services/apiService';
import { userItemsPerListMixin } from '@/utils/vuex';
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
const defaultItemsPerPage = 10;
const MAX_DEPENDENCIES_FETCH_LIMIT = 100;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreConnectorsList',
  mixins: [userItemsPerListMixin],
  props: {
    accounts: {
      type: Array,
      required: false,
      default: () => []
    },
    tenants: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data: function () {
    return {
      cmOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      },
      tableOptions: {
        ...defaultTableOptions
      },
      isLoadingConnectors: true,
      hasDataBeenLoaded: false,
      isInitialLoad: true,
      hasAnyConnectors: false,
      searchItem: '',
      isDestroyed: false,
      connectorDialog: false,
      editConnectorIndex: -1,
      deleteDialog: false,
      deleteBridgeId: null,
      newBridgeType: null,
      bridgeTypes: [],
      bridges: [],
      bridgeValidations: {},
      totalItemsCount: 0,
      connectorDependencies: null,
      connectorDependenciesFetched: false,
      integrationFlowsTree: null,
      digitalTwinsTree: null,
      virtualDevicesTree: null,
      deviceTemplatesTree: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    canManageIntegrations() {
      return !!get(this.permissions, 'Integration.manage');
    },
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    bridgeTypeMap() {
      const map = {};
      this.bridgeTypes.forEach(type => {
        map[type.type] = type;
      });
      return map;
    },
    tableLocalizations() {
      return {
        headers: [{
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.id'),
          value: 'id',
          align: 'right',
          sortable: true
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.state'),
          value: 'state',
          sortable: true
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.name'),
          value: 'name',
          sortable: true
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.type'),
          value: 'type',
          sortable: true
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.kind'),
          value: 'table.kind',
          align: 'center',
          sortable: false
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.account'),
          value: 'tenantId',
          sortable: true
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.createdAt'),
          value: 'createdAt',
          sortable: true
        }, {
          text: '',
          value: '',
          align: 'end',
          sortable: false,
          width: '78'
        }],
        footer: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [10, 20, 50]
        }
      };
    },
    runtimeInstance() {
      return this;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    hasNoConnectors() {
      return this.hasDataBeenLoaded && !this.isLoadingConnectors && !this.isSearching && !this.hasAnyConnectors;
    },
    isSearching() {
      return this.searchItem && this.searchItem.trim().length > 0;
    },
    shouldShowTable() {
      return this.hasAnyConnectors || this.isLoadingConnectors;
    },
    tableBridges() {
      return this.bridges.map(bridge => {
        const config = this.bridgeTypeMap[bridge.type];
        const inbound = get(config, 'manifest.functionScope.invokeDirection.inbound');
        const outbound = get(config, 'manifest.functionScope.invokeDirection.outbound');
        return {
          ...bridge,
          table: {
            kind: inbound && outbound ? 'both' : inbound ? 'inbound' : 'outbound'
          }
        };
      });
    },
    deleteButtonEnabled() {
      if (!this.connectorDependenciesFetched) {
        return false;
      }
      const deps = this.connectorDependencies;
      if (!deps) {
        return false;
      }
      const keys = ['deviceTemplates', 'digitalTwins', 'virtualDevices', 'integrationFlows'];
      return keys.every(k => Array.isArray(deps[k]) && deps[k].length === 0);
    },
    hasConnectorDependencies() {
      const deps = this.connectorDependencies;
      if (!deps) {
        return false;
      }
      const dependencyTypes = ['deviceTemplates', 'digitalTwins', 'integrationFlows', 'virtualDevices'];
      if (dependencyTypes.some(d => deps[d] && deps[d].length)) {
        return true;
      }
      return false;
    }
  },
  watch: {
    tenants() {
      if (!this.isDestroyed) {
        this.tableOptions.page = 1;
        this.loadData();
      }
    },
    searchItem: debounce(function (newVal, oldVal) {
      if (!this.isDestroyed) {
        this.tableOptions.page = 1;
        const wasSearching = oldVal && oldVal.trim().length > 0;
        const isSearching = newVal && newVal.trim().length > 0;
        if (wasSearching && !isSearching) {
          this.isInitialLoad = false;
        }
        this.loadData();
      }
    }, 500),
    tableOptions: {
      handler: function (newVal, oldVal) {
        if (this.isDestroyed) {
          return;
        }
        if (newVal.itemsPerPage !== oldVal.itemsPerPage) {
          if (this.totalItemsCount > 0) {
            const maxPage = Math.ceil(this.totalItemsCount / newVal.itemsPerPage);
            if (newVal.page > maxPage) {
              this.tableOptions.page = 1;
            }
          }
        }
        const pageChanged = newVal.page !== oldVal.page;
        const pageSizeChanged = newVal.itemsPerPage !== oldVal.itemsPerPage;
        const sortByChanged = JSON.stringify(newVal.sortBy) !== JSON.stringify(oldVal.sortBy);
        const sortDescChanged = JSON.stringify(newVal.sortDesc) !== JSON.stringify(oldVal.sortDesc);
        if (pageChanged || pageSizeChanged || sortByChanged || sortDescChanged) {
          this.loadData();
        }
      },
      deep: true
    }
  },
  created() {
    const page = parseInt(this.$route.query.page, 10) || defaultTableOptions.page;
    this.tableOptions = {
      ...this.tableOptions,
      page
    };
    const searchFromQuery = this.$route.query.search || '';
    if (searchFromQuery) {
      this.searchItem = searchFromQuery;
    }
  },
  async mounted() {
    await this.itemsPerPageReadyPromise;
    this.loadData();
  },
  beforeDestroy() {
    this.isDestroyed = true;
  },
  methods: {
    get,
    showCreateDialog() {
      this.$emit('create');
    },
    async handleDeleteConnector(id) {
      this.deleteDialog = false;
      try {
        await this.deleteBridge(id);
      } catch (error) {
        // already handled inside deleteBridge
      }
    },
    loadData() {
      if (this.isDestroyed) {
        return;
      }
      this.bridgeTypes = [];
      this.bridges = [];
      this.totalItemsCount = 0;
      this.loadBridgeTypes().then(types => {
        if (this.isDestroyed) {
          return;
        }
        this.bridgeTypes = types.map(type => {
          return {
            ...type,
            name: get(type, 'manifest.displayName')
          };
        }).sort((b1, b2) => {
          if (b1.name > b2.name) {
            return 1;
          }
          if (b1.name < b2.name) {
            return -1;
          }
          return 0;
        });
        return this.loadBridgeConfigs();
      }).catch(error => {
        console.error('Error loading bridge types:', error);
        return this.loadBridgeConfigs();
      });
    },
    bridgeConfigIsInbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.inbound', false);
    },
    bridgeConfigIsOutbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.outbound', false);
    },
    bridgeConfig(bridge) {
      return this.bridgeTypeMap[bridge.type];
    },
    bridgeConfigKeys(instance) {
      const bridgeManifest = this.bridgeConfig(instance).manifest;
      let keys = Object.keys(bridgeManifest.config[0]).filter(key => {
        if (get(bridgeManifest, `config[0][${key}].hidden`)) {
          return false;
        }
        // if field is readonly, only show if it has a value
        if (get(bridgeManifest, `config[0][${key}].readonly`)) {
          return !!instance.bridge.config[key];
        }
        const visibleConfig = get(bridgeManifest, `config[0][${key}].visible`) || {};
        return Object.keys(visibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] === visibleConfig[dependentKey];
        });
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(bridgeManifest, `config[0][${a}].orderIdx`) || 0;
        const orderIdxB = get(bridgeManifest, `config[0][${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    loadBridgeTypes() {
      if (this.isDestroyed) {
        return Promise.resolve([]);
      }
      return ApiService.getBridgeTypes().then(bridgeTypes => {
        if (this.isDestroyed) {
          return [];
        }
        return bridgeTypes;
      });
    },
    loadBridgeConfigs() {
      if (this.isDestroyed) {
        return Promise.resolve([]);
      }
      if (!this.tenants) {
        this.tenants = [];
      }
      const ids = this.tenants.map(acc => acc.id);
      this.isLoadingConnectors = true;
      const sortBy = this.tableOptions.sortBy && this.tableOptions.sortBy.length > 0 ? this.tableOptions.sortBy[0] : 'id';
      const sortDesc = this.tableOptions.sortDesc && this.tableOptions.sortDesc.length > 0 ? this.tableOptions.sortDesc[0] : false;
      return ApiService.getBridgesForTenantsPaginated(ids, this.tableOptions.page, this.tableOptions.itemsPerPage, true, this.searchItem, sortBy, sortDesc).then(response => {
        if (this.isDestroyed) {
          return [];
        }
        const configs = response.data || [];
        this.totalItemsCount = response.pagination ? response.pagination.totalItems : configs.length;
        if (this.isInitialLoad || !this.isSearching) {
          this.hasAnyConnectors = this.totalItemsCount > 0;
        }
        const bridgesWithDisplayNames = configs.map(config => {
          const bridgeType = this.bridgeTypeMap[config.type];
          config.displayName = bridgeType ? get(bridgeType, 'manifest.displayName') : '';
          return config;
        });
        this.bridges = bridgesWithDisplayNames;
        this.isLoadingConnectors = false;
        this.hasDataBeenLoaded = true;
        this.isInitialLoad = false;
        return bridgesWithDisplayNames;
      }).catch(error => {
        if (!this.isDestroyed) {
          console.error('Error loading bridge configs:', error);
          const wasInitialLoad = this.isInitialLoad;
          const wasSearching = this.isSearching;
          this.isLoadingConnectors = false;
          this.bridges = [];
          this.totalItemsCount = 0;
          this.hasDataBeenLoaded = true;
          this.isInitialLoad = false;
          if (wasInitialLoad || !wasSearching) {
            this.hasAnyConnectors = false;
          }
        }
        return [];
      });
    },
    async deleteBridge(id) {
      const bridge = this.bridges.find(b => b.id === id);
      if (!bridge || !bridge.id) {
        return;
      }
      try {
        await ApiService.deleteBridge(bridge.id);
        this.$snack(this.$i18n_t('Connectors.Connectors.actions.deleted'), 'success');
        this.loadData();
      } catch (error) {
        this.$snack(error.message || this.$i18n_t('Common.error'), 'error');
      }
    },
    localizeDate(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).format('L LTS');
      }
      return '-';
    },
    async openDeleteDialog(id) {
      this.deleteDialog = true;
      await this.fetchConnectorDependencies(id);
    },
    fetchConnectorDependencies(bridgeId) {
      this.connectorDependenciesFetched = false;
      this.connectorDependencies = null;
      return ApiService.bridgeGetDependencies(bridgeId, MAX_DEPENDENCIES_FETCH_LIMIT).then(connectorDependencies => {
        this.connectorDependencies = connectorDependencies;
        this.integrationFlowsTree = this.buildDependencyTree(connectorDependencies, 'integrationFlows', 'accMgmt.tenant.deleteConfimationIntegrationFlows');
        this.digitalTwinsTree = this.buildDependencyTree(connectorDependencies, 'digitalTwins', 'accMgmt.tenant.deleteConfimationDigitalTwins');
        this.virtualDevicesTree = this.buildDependencyTree(connectorDependencies, 'virtualDevices', 'accMgmt.tenant.deleteConfimationVirtualDevices');
        this.deviceTemplatesTree = this.buildDependencyTree(connectorDependencies, 'deviceTemplates', 'accMgmt.tenant.deleteConfimationDeviceTemplates');
        this.connectorDependenciesFetched = true;
      }).catch(error => {
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
      });
    },
    buildDependencyTree(data, listKey, i18nKey) {
      const children = data[listKey] || [];
      const hasMore = children.length >= MAX_DEPENDENCIES_FETCH_LIMIT;
      const rawTitle = this.$i18n_tc(i18nKey, children.length, {
        n: children.length
      });
      const title = hasMore ? rawTitle.replace(`${children.length}`, `${children.length}+`) : rawTitle;
      const icon = listKey === 'deviceTemplates' ? 'sitemap' : listKey === 'virtualDevices' ? 'sensor-on' : listKey === 'digitalTwins' ? 'dice-d8' : 'angle-double-right';
      return [{
        id: null,
        parentId: null,
        title,
        children,
        icon
      }];
    }
  }
};